import React, { useCallback, useEffect, useState } from "react";

import {
  AnyFilterExpressionInput,
  MdmAntennaDto,
  SortDirection,
  SortInput,
  useGetAntennasByProjectPagedLazyQuery,
} from "gql/generated/types";
import { T } from "services/lang/LanguageService";
import { Button } from "primereact/button";
import { Link } from "react-router-dom";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import { TableColumn } from "../utils/types";
import { apolloErrorToaster, searchAntennasFilter } from "../utils/utils";
import useCustomColumns from "../hooks/useCustomColumns";
import { initialFilter } from "../utils/const";
import TableColumnSelection from "../components/TableColumnSelection";
import LazyTable from "../components/LazyTable";
import AntennaFormDialog from "../components/Antenna/AntennaFormDialog";

const SORT_FIELD = "title";

const Antennas: React.FC = () => {
  const { projectId, userSettings } = useProjectContext();
  const [showDialog, setShowDialog] = useState(false);

  const [tableOptions, setTableOptions] = useTableFilter("Antennas", {
    sortField: SORT_FIELD,
    reqSortField: SORT_FIELD,
    sortOrder: 1,
  });

  const [getNextAntennas, { data, loading, previousData }] = useGetAntennasByProjectPagedLazyQuery({
    onError: apolloErrorToaster,
  });

  const [getIsolatedData] = useGetAntennasByProjectPagedLazyQuery();

  // useEffect(() => {
  //   if (userSettings) {
  //     getNextAntennas({
  //       variables: {
  //         input: {
  //           projectId,
  //           take: tableDetails?.rows || 10,
  //           skip: tableOptions.first,
  //           sort: [
  //             {
  //               property: tableOptions.reqSortField,
  //               direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
  //             },
  //           ],
  //           andFilters: tableOptions.andFilters,
  //           filter: tableOptions.filter,
  //         },
  //       },
  //     });
  //   }
  // }, [userSettings, projectId, tableOptions, tableDetails, getNextAntennas]);

  const antennaInfoBtn = (rowData: MdmAntennaDto) => (
    <Link to={rowData.id!} title={T("mdm.object-info")}>
      {rowData.title}
    </Link>
  );

  const columns: TableColumn<MdmAntennaDto>[] = [
    {
      field: "title",
      columnKey: "title",
      body: antennaInfoBtn,
      header: T("word.title"),
      sortable: true,
    },
    {
      field: "description",
      columnKey: "description",
      header: T("word.description"),
      sortable: true,
    },
    {
      field: "city",
      columnKey: "city",
      header: T("word.city"),
      sortable: true,
    },
    {
      field: "street",
      columnKey: "street",
      header: T("word.street"),
      sortable: true,
    },
    {
      field: "houseNumber",
      columnKey: "houseNumber",
      header: T("word.house-number"),
      sortable: true,
    },
  ];

  const antennasData = (data?.allAntennasByProjectIdPaged.items ??
    previousData?.allAntennasByProjectIdPaged.items ??
    []) as MdmAntennaDto[];

  const total =
    data?.allAntennasByProjectIdPaged.pageInfo?.total ?? previousData?.allAntennasByProjectIdPaged.pageInfo?.total ?? 0;

  const [customColumns, setCustomColumns, formattedColumns, mapCols, formatObjectFields, handleColumnSave, updateLoading] =
    useCustomColumns("Antennas", columns, antennasData, tableOptions.rows);

  const fetchData = useCallback(
    async (
      take: number | null = null,
      skip: number | null = null,
      sort: SortInput[] | null = null,
      filters: AnyFilterExpressionInput[] | null = null,
      cacheAndNetwork: boolean = false,
      filter: AnyFilterExpressionInput | null = null,
      isolatedFetch: boolean = false
    ) => {
      const fetchFn = isolatedFetch ? getIsolatedData : getNextAntennas;
      const resp = await fetchFn({
        variables: {
          input: {
            projectId,
            take: take ?? tableOptions.rows,
            skip: skip ?? tableOptions.first,
            sort: sort ?? [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: filters ?? tableOptions.andFilters,
            filter: filter ?? tableOptions.filter,
          },
        },
        fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
      });
      return resp.data;
    },
    [
      getIsolatedData,
      getNextAntennas,
      projectId,
      tableOptions.andFilters,
      tableOptions.filter,
      tableOptions.first,
      tableOptions.reqSortField,
      tableOptions.rows,
      tableOptions.sortOrder,
    ]
  );

  useEffect(() => {
    if (userSettings) {
      fetchData(tableOptions.rows);
    }
  }, [tableOptions, userSettings, fetchData]);

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: SORT_FIELD,
      reqSortField: SORT_FIELD,
      sortOrder: -1,
    }));
  };

  const handleSearch = (value: string) => {
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchAntennasFilter(value) : null,
      first: 0,
      search: value,
    }));
  };

  const extraTools = (
    <>
      <TableColumnSelection
        customColumns={customColumns}
        tableName="Antennas"
        setCustomColumns={setCustomColumns}
        handleColumnSave={handleColumnSave}
        mapCol={mapCols}
        loading={updateLoading}
      />
      <Button type="button" size="small" label={T("action.create-new")} icon="pi pi-plus" onClick={() => setShowDialog(true)} />
    </>
  );

  return (
    <>
      <LazyTable
        columns={formattedColumns}
        data={formatObjectFields}
        tableName="Maintenance"
        buttons={extraTools}
        containerName="MdmMaintenance"
        loading={loading}
        setTableOptions={setTableOptions}
        resetFilters={resetFilters}
        tableOptions={tableOptions}
        totalItems={total}
        fetchData={fetchData}
        onSearch={handleSearch}
        setCustomColumns={setCustomColumns}
      />
      <AntennaFormDialog showDialog={showDialog} setShowDialog={setShowDialog} />
    </>
  );
};

export default Antennas;
